'use client';

import { c as _c } from "react/compiler-runtime";
import { Toaster as ChakraToaster, Box, Button, Portal, Spinner, Stack, Toast, createToaster } from '@chakra-ui/react';
import { useState } from 'react';
import { LuMaximize2, LuX } from 'react-icons/lu';
import { DialogBody, DialogCloseTrigger, DialogContent, DialogHeader, DialogRoot, DialogTitle } from './dialog';

/**
 * No group-wide `duration` on purpose — see the comment in
 * `src/utils/toastDurations.ts`. Setting one lengthens success toasts, which
 * stack over the digraph canvas and eat the drags in `layout.spec.ts`.
 */
export const toaster = createToaster({
  placement: 'bottom-end',
  pauseOnPageIdle: true
});

// Some descriptions are huge — e.g. a raw Postgres/Drizzle error carries the
// full failed SQL plus every bound parameter (tens of thousands of UUIDs).
// Above this length we keep the toast compact (scrollable body) and offer a
// fullscreen view so the message is still fully readable.
const LONG_DESCRIPTION_THRESHOLD = 280;
type ExpandedDescription = {
  title: string;
  description: string;
};
export const Toaster = () => {
  const $ = _c(13);
  const [expanded, setExpanded] = useState(null);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Portal><ChakraToaster toaster={toaster} insetInline={{
        mdDown: "4"
      }}>{toast => {
          const description = typeof toast.description === "string" ? toast.description : undefined;
          const title = typeof toast.title === "string" ? toast.title : undefined;
          const isLong = description !== undefined && description.length > LONG_DESCRIPTION_THRESHOLD;
          return <Toast.Root width={{
            md: "sm"
          }}>{toast.type === "loading" ? <Spinner size="sm" color="blue.solid" /> : <Toast.Indicator />}<Stack gap="1" flex="1" maxWidth="100%">{toast.title && <Toast.Title>{toast.title}</Toast.Title>}{toast.description && (description !== undefined ? <Toast.Description asChild={true}><Box maxH="9rem" width="100%" overflowY="auto" overflowX="hidden" whiteSpace="pre-wrap" wordBreak="break-word" fontFamily={isLong ? "mono" : undefined} fontSize={isLong ? "xs" : undefined}>{description}</Box></Toast.Description> : <Toast.Description>{toast.description}</Toast.Description>)}{isLong && <Button size="xs" variant="subtle" alignSelf="flex-start" onClick={() => setExpanded({
                title: title ?? "Details",
                description: description ?? ""
              })}><LuMaximize2 />Expand</Button>}</Stack>{toast.action && <Toast.ActionTrigger>{toast.action.label}</Toast.ActionTrigger>}{toast.meta?.closable && <Toast.CloseTrigger cursor="pointer"><LuX /></Toast.CloseTrigger>}</Toast.Root>;
        }}</ChakraToaster></Portal>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const t1 = expanded !== null;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = e => {
      if (!e.open) {
        setExpanded(null);
      }
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const t3 = expanded?.title ?? "Details";
  let t4;
  if ($[2] !== t3) {
    t4 = <DialogHeader><DialogTitle>{t3}</DialogTitle></DialogHeader>;
    $[2] = t3;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const t5 = expanded?.description;
  let t6;
  if ($[4] !== t5) {
    t6 = <DialogBody><Box as="pre" whiteSpace="pre-wrap" wordBreak="break-word" fontFamily="mono" fontSize="sm">{t5}</Box></DialogBody>;
    $[4] = t5;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  let t7;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <DialogCloseTrigger />;
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  let t8;
  if ($[7] !== t4 || $[8] !== t6) {
    t8 = <DialogContent>{t4}{t6}{t7}</DialogContent>;
    $[7] = t4;
    $[8] = t6;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  let t9;
  if ($[10] !== t1 || $[11] !== t8) {
    t9 = <>{t0}<DialogRoot open={t1} onOpenChange={t2} size="full" scrollBehavior="inside">{t8}</DialogRoot></>;
    $[10] = t1;
    $[11] = t8;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  return t9;
};